import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "../components/motion";
import { PageHero, CtaBand, FaqList } from "../components/shared";
import { QuoteLink } from "../components/QuotePanel";
import { imgProps } from "../components/media";
import { SERVICES, PI_INCLUDES, FAQS, img, dim } from "../data/site";

const TABS = [
  ["armata", "Vigilanza armata"],
  ["nonarmata", "Vigilanza non armata"],
];

function SvcRow({ svc, kind }) {
  const flip = svc.img.endsWith(".svg") ? false : kind === "Vigilanza non armata";
  const isIll = svc.img.endsWith(".svg");
  const detail = `/servizi/${svc.id}`;
  return (
    <Reveal className={`svc-row ${flip ? "flip" : ""}`} data-testid={`svc-${svc.id}`}>
      <div className={isIll ? "illpanel" : "phframe tall"}>
        <Link to={detail} aria-label={`${svc.name}, pagina di dettaglio`} data-testid={`svc-${svc.id}-link`}>
          <img {...imgProps(svc.img, { sizes: "(max-width: 760px) 92vw, 560px" })} alt={svc.name} />
        </Link>
        {isIll && (
          <p style={{ position: "absolute", bottom: 12, left: 0, right: 0, textAlign: "center", fontSize: 12, color: "var(--muted)", margin: 0, zIndex: 1 }}>
            Illustrazione provvisoria: foto in arrivo
          </p>
        )}
      </div>
      <div className="svc-info">
        <span className="svc-kind">{kind}</span>
        <h3 className="h3">
          <Link to={detail} style={{ color: "inherit", textDecoration: "none" }} data-testid={`svc-${svc.id}-title`}>
            {svc.name}
          </Link>
        </h3>
        <p style={{ color: "var(--muted)", margin: 0 }}>{svc.desc}</p>
        <p className="ideale">
          <strong>Ideale per</strong> — {svc.ideale}
        </p>
        <p style={{ margin: "22px 0 0", fontWeight: 700 }}>Cosa include</p>
        <ul className="includes">
          {svc.inc.map((x) => (
            <li key={x}>
              <Check size={17} className="ck" aria-hidden="true" /> {x}
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <Link to={detail} className="btn white sm" style={{ boxShadow: "inset 0 0 0 2px var(--line)" }} data-testid={`svc-${svc.id}-scopri`}>
            Scopri di più
          </Link>
          <QuoteLink servizio={svc.id} className="btn dark sm" testid={`svc-${svc.id}-cta`}>
            Preventivo per questo servizio
          </QuoteLink>
        </div>
      </div>
    </Reveal>
  );
}

export default function Servizi() {
  const [tab, setTab] = useState("armata");
  const reduce = useReducedMotion();
  return (
    <>
      <PageHero
        kick="Servizi"
        lines={["Vigilanza armata e non", "armata, su misura."]}
        sub="Quattordici servizi, un unico standard: guardie particolari giurate e personale fiduciario selezionato, coordinati da due centrali operative certificate."
        ph={img("guardia-presidio-orizzontale.jpg")}
        ctas={
          <QuoteLink className="btn" testid="servizi-hero-cta">
            Richiedi un preventivo
          </QuoteLink>
        }
      />

      <section className="sec" data-testid="servizi-list">
        <h2 className="sr-only">Elenco dei servizi di vigilanza</h2>
        <div className="wrap">
          <Reveal className="tabs" style={{ marginBottom: 10 }}>
            {TABS.map(([k, label]) => (
              <button key={k} type="button" className={`tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`servizi-tab-${k}`}>
                {label}
              </button>
            ))}
          </Reveal>
          {/* Entrambe le schede restano nell'HTML (SEO/prerender): quella inattiva è solo nascosta */}
          {TABS.map(([k, label]) => (
            <motion.div
              key={k}
              initial={false}
              animate={{ opacity: tab === k ? 1 : 0 }}
              transition={{ duration: reduce ? 0 : 0.3 }}
              hidden={tab !== k}
              data-testid={`servizi-pane-${k}`}
            >
              {SERVICES[k].map((svc) => (
                <SvcRow key={svc.id} svc={svc} kind={label} />
              ))}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="sec-blue sec" data-testid="servizi-metodo">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Come funziona</p>
            <h2 className="h2">Come costruiamo il tuo servizio</h2>
          </Reveal>
          <div className="minis">
            {[
              ["Sopralluogo gratuito", "Un responsabile visita la struttura e ascolta le tue esigenze."],
              ["Analisi del rischio", "Punti critici, orari esposti e procedure necessarie, messi per iscritto."],
              ["Proposta su misura", "Uomini, tecnologia e centrale combinati in un unico servizio."],
              ["Rapporti costanti", "Resoconti dei controlli e un referente sempre raggiungibile."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="mini" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.16)" }} data-testid={`metodo-${i}`}>
                  <span style={{ fontFamily: "var(--arch)", fontWeight: 800, fontSize: 14, letterSpacing: ".18em", color: "var(--gold)", display: "block", marginBottom: 14 }}>0{i + 1}</span>
                  <h3 style={{ color: "#fff" }}>{t}</h3>
                  <p style={{ color: "#D9DEE6" }}>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" data-testid="servizi-incluso">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Sempre compreso</p>
            <h2 className="h2">Ogni servizio, stesso rigore.</h2>
          </Reveal>
          <div className="minis">
            {PI_INCLUDES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="mini" data-testid={`incluso-${i}`}>
                  <div className="mic">
                    <Check size={20} aria-hidden="true" />
                  </div>
                  <h3>{t}</h3>
                  <p>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="servizi-faq">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "40px 90px" }}>
          <Reveal style={{ flex: "1 1 360px" }}>
            <p className="kick">Domande frequenti</p>
            <h2 className="h2">Domande sui servizi</h2>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "2 1 560px", minWidth: 0 }}>
            <FaqList items={FAQS.servizi} testid="servizi-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand testid="servizi-cta" />
    </>
  );
}
